// src/pages/Dashboard.jsx
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    Card,
    CardActionArea,
    CardContent,
    Grid,
    Typography,
    CircularProgress,
    Box,
    Alert,
    Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// 📈 Recharts
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';

import { adminGet } from '../config/api';
import StatusChipRow from '../components/StatusChipRow';

const DASHBOARD_STATUS_CONFIG = [
    { status: 'PENDING', label: 'P', color: 'warning' },
    { status: 'VALIDATED', label: 'V', color: 'success' },
    { status: 'REJECTED', label: 'R', color: 'error' },
];

const Tile = ({ label, value, to, children }) => {
    const navigate = useNavigate();
    return (
        <Card elevation={1} sx={{ borderRadius: 2 }}>
            <CardActionArea onClick={() => to && navigate(to)}>
                <CardContent>
                    <Typography variant="overline" color="text.secondary">{label}</Typography>
                    <Typography variant="h4" sx={{ mt: 0.5 }}>{value}</Typography>
                    {children}
                    {to && (
                        <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                            Voir la liste →
                        </Typography>
                    )}
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

/**
 * Normalisation “souple” du format backend.
 * On accepte par entrée au choix:
 *  - { weekLabel: "2025-W42", count: 12 }
 *  - { week: "2025-W42", count: 12 }
 *  - { start: "2025-10-14", count: 12 }
 *  - { date: "2025-10-14", count: 12 }
 */
function normalizeWeeklyData(arr = []) {
    return arr.map((it) => {
        const rawLabel =
            it.weekLabel || it.week || it.start || it.date || it.label || '?';
        return {
            label: String(rawLabel),
            count: Number(it.count ?? it.value ?? 0),
        };
    });
}

function RdfWeeklyChart({ weeks = 12 }) {
    const [series, setSeries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');

    useEffect(() => {
        let cancelled = false;

        async function load() {
            setLoading(true);
            setErr('');
            try {
                let data;
                try {
                    data = await adminGet(`/stats/rdf-weekly?weeks=${weeks}`);
                } catch (weeklyError) {
                    console.error(weeklyError);
                    const fallbackStats = await adminGet('/stats');
                    data = fallbackStats?.rdfWeekly || fallbackStats?.rdfPerWeek || [];
                }

                const normalized = normalizeWeeklyData(Array.isArray(data) ? data : []);
                if (!cancelled) setSeries(normalized);
            } catch (e) {
                console.error(e);
                if (!cancelled) {
                    setErr("Impossible de charger la série hebdomadaire.");
                    setSeries([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [weeks]);

    return (
        <Card elevation={1} sx={{ borderRadius: 2 }}>
            <CardContent>
                <Typography variant="overline" color="text.secondary">
                    RDF créés par semaine
                </Typography>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Dernières {weeks} semaines
                </Typography>
                {err && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        {err}
                    </Alert>
                )}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : series.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Aucune donnée à afficher.
                    </Typography>
                ) : (
                    <Box sx={{ width: '100%', height: 260 }}>
                        <ResponsiveContainer>
                            <BarChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="label"
                                    tick={{ fontSize: 12 }}
                                    interval="preserveStartEnd"
                                />
                                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                                <Tooltip
                                    formatter={(value) => [value, 'RDF']}
                                    labelFormatter={(label) => `Semaine: ${label}`}
                                />
                                <Bar dataKey="count" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        adminGet('/stats')
            .then((payload) => {
                if (!cancelled) setStats(payload);
            })
            .catch((e) => {
                console.error(e);
                if (!cancelled) setStats(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                Tableau de bord
            </Typography>

            {/* Ligne 1 — Graphe sur 12 semaines */}
            <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12}>
                    <RdfWeeklyChart weeks={12} />
                </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Ligne 2 — Tuiles chiffrées */}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                    <Tile label="RDF" value={stats?.rdf ?? 0} to="/rdf">
                        <StatusChipRow
                            counts={stats?.rdfByStatus}
                            config={DASHBOARD_STATUS_CONFIG}
                            showTotal={false}
                            sx={{ mt: 1 }}
                        />
                    </Tile>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Tile label="Subjects" value={stats?.subjects ?? 0} to="/subjects" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Tile label="Oobjects" value={stats?.oobjects ?? 0} to="/oobjects" />
                </Grid>
            </Grid>
        </Box>
    );
}
